import { useEffect, useRef, useState } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Upload, Scissors, Loader2, X, Play, Video } from "lucide-react";
import { toast } from "sonner";

const CORE_BASE = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd";
const MAX_DURATION = 30;

// FFmpeg-based video trimmer: user selects a video, previews, chooses a 30-sec window,
// and we transcode to a browser-friendly MP4 clip. Returned as data URL for base64 storage.
export default function VideoTrimmer({ value, onChange }) {
  const [ready, setReady] = useState(false);
  const [loadingCore, setLoadingCore] = useState(false);
  const [file, setFile] = useState(null);
  const [srcUrl, setSrcUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(MAX_DURATION);
  const [trimming, setTrimming] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);
  const ffmpegRef = useRef(null);

  useEffect(() => () => { if (srcUrl) URL.revokeObjectURL(srcUrl); }, [srcUrl]);

  const loadFFmpeg = async () => {
    if (ready || loadingCore) return;
    setLoadingCore(true);
    try {
      const ff = new FFmpeg();
      ff.on("progress", ({ progress }) => setProgress(Math.min(1, progress || 0)));
      await ff.load({
        coreURL: await toBlobURL(`${CORE_BASE}/ffmpeg-core.js`, "text/javascript"),
        wasmURL: await toBlobURL(`${CORE_BASE}/ffmpeg-core.wasm`, "application/wasm"),
      });
      ffmpegRef.current = ff;
      setReady(true);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load video processor. Please refresh and retry.");
    } finally {
      setLoadingCore(false);
    }
  };

  const onFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Please choose a video file."); return; }
    if (f.size > 200 * 1024 * 1024) { toast.error("Video is over 200 MB — please compress first."); return; }
    setFile(f);
    const url = URL.createObjectURL(f);
    setSrcUrl(url);
    loadFFmpeg();
  };

  const onMeta = () => {
    const d = videoRef.current?.duration || 0;
    setDuration(d);
    setStart(0);
    setEnd(Math.min(MAX_DURATION, d));
  };

  const trim = async () => {
    if (!file || !ffmpegRef.current) return;
    if (end - start > MAX_DURATION) { toast.error(`Clip must be ${MAX_DURATION}s or less.`); return; }
    setTrimming(true);
    setProgress(0);
    try {
      const ff = ffmpegRef.current;
      const inName = "in.mp4";
      const outName = "out.mp4";
      await ff.writeFile(inName, await fetchFile(file));
      await ff.exec([
        "-ss", start.toFixed(2),
        "-i", inName,
        "-t", (end - start).toFixed(2),
        "-vf", "scale='min(1280,iw)':-2",
        "-c:v", "libx264", "-preset", "veryfast", "-crf", "28",
        "-c:a", "aac", "-b:a", "96k",
        "-movflags", "+faststart",
        "-y", outName,
      ]);
      const out = await ff.readFile(outName);
      const blob = new Blob([out.buffer], { type: "video/mp4" });
      // Convert to data URL (base64) so we can POST as JSON
      const reader = new FileReader();
      reader.onload = () => {
        onChange(reader.result); // data:video/mp4;base64,...
        toast.success(`Trimmed to ${(end - start).toFixed(1)}s`);
        setTrimming(false);
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      console.error(e);
      toast.error("Trimming failed. Try a smaller video or different format.");
      setTrimming(false);
    }
  };

  const clear = () => { setFile(null); setSrcUrl(null); setDuration(0); onChange(""); };

  if (value && !file) {
    return (
      <div className="space-y-3" data-testid="video-trimmer-preview">
        <div className="relative aspect-video rounded-2xl overflow-hidden bg-black">
          <video src={value} controls className="w-full h-full" />
          <button onClick={() => onChange("")} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-background/90 grid place-items-center" data-testid="video-clear-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5"><Video className="w-3 h-3" /> Reel attached · will play on your listing hero</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="video-trimmer">
      {!file ? (
        <label className="block border-2 border-dashed border-border rounded-2xl p-8 text-center cursor-pointer hover:bg-muted/40 transition" data-testid="video-upload-zone">
          <input type="file" accept="video/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="video-upload-input" />
          <Upload className="w-6 h-6 mx-auto text-primary" />
          <div className="mt-3 font-display text-lg">Upload a video walk-through</div>
          <div className="text-xs text-muted-foreground mt-1">MP4/MOV up to 200 MB — we&apos;ll trim it to 30 seconds in your browser</div>
        </label>
      ) : (
        <>
          <div className="relative aspect-video rounded-2xl overflow-hidden bg-black border border-border">
            <video
              ref={videoRef}
              src={srcUrl}
              onLoadedMetadata={onMeta}
              onTimeUpdate={(e) => { if (e.target.currentTime >= end) e.target.pause(); }}
              controls
              className="w-full h-full"
              data-testid="video-preview"
            />
            <button onClick={clear} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-background/90 grid place-items-center" data-testid="video-remove-btn">
              <X className="w-4 h-4" />
            </button>
          </div>

          {loadingCore && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin text-primary" /> Loading video processor…
            </div>
          )}

          {duration > 0 && (
            <div className="bg-muted/40 rounded-2xl p-5 space-y-4" data-testid="trim-controls">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Trim to 30-second reel</span>
                <span className="font-mono font-semibold">{(end - start).toFixed(1)}s selected</span>
              </div>
              <div>
                <div className="flex items-center justify-between text-xs mb-2">
                  <span>Start · {start.toFixed(1)}s</span>
                  <span>End · {end.toFixed(1)}s</span>
                </div>
                <Slider
                  value={[start, end]}
                  onValueChange={(v) => {
                    let [s, e] = v;
                    if (e - s > MAX_DURATION) {
                      if (s !== start) s = e - MAX_DURATION;
                      else e = s + MAX_DURATION;
                    }
                    setStart(Math.max(0, s));
                    setEnd(Math.min(duration, e));
                    if (videoRef.current) videoRef.current.currentTime = s;
                  }}
                  min={0}
                  max={duration}
                  step={0.1}
                  data-testid="trim-slider"
                />
                <div className="mt-1.5 text-[10px] text-muted-foreground flex justify-between font-mono">
                  <span>0s</span><span>{duration.toFixed(1)}s</span>
                </div>
              </div>
              <Button
                onClick={trim}
                disabled={!ready || trimming}
                className="w-full rounded-full bg-primary hover:bg-primary/90"
                data-testid="trim-confirm-btn"
              >
                {trimming ? <><Loader2 className="w-4 h-4 animate-spin" /> Trimming…</> : <><Scissors className="w-4 h-4" /> Trim & attach reel</>}
              </Button>
              {trimming && <Progress value={progress * 100} data-testid="trim-progress" />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
